import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';

const NotificationBell = () => {
  const [alertas, setAlertas] = useState([]);
  const [showPanel, setShowPanel] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAlertas();
    // Poll every 30 seconds
    const interval = setInterval(fetchAlertas, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlertas = async () => {
    try {
      const response = await api.get('/alertas?only_unread=false');
      setAlertas(response.data);
      const unread = response.data.filter(a => !a.leida).length;
      setUnreadCount(unread);
    } catch (error) {
      // Silently fail if not logged in
    }
  };

  const handleAlertaClick = async (alerta) => {
    try {
      // Marcar como leída
      await api.put(`/alertas/${alerta.id}/marcar-leida`);
      
      // Navegar a la URL de acción
      if (alerta.action_url) {
        navigate(alerta.action_url);
      }
      
      setShowPanel(false);
      fetchAlertas();
    } catch (error) {
      toast.error('Error al procesar alerta');
    }
  };

  const getAlertIcon = (tipo) => {
    switch (tipo) {
      case 'PSP Aceptada - Área':
        return '🔔';
      case 'PSP Aceptada - Finanzas':
        return '💰';
      case 'Seguimiento Comercial':
        return '⏳';
      default:
        return '📢';
    }
  };

  const getAlertColor = (tipo) => {
    switch (tipo) {
      case 'PSP Aceptada - Área':
        return 'bg-blue-50 border-blue-200';
      case 'PSP Aceptada - Finanzas':
        return 'bg-green-50 border-green-200';
      case 'Seguimiento Comercial':
        return 'bg-orange-50 border-orange-200';
      default:
        return 'bg-slate-50 border-slate-200';
    }
  };

  return (
    <div className='relative'>
      <Button
        variant='ghost'
        size='sm'
        onClick={() => setShowPanel(!showPanel)}
        className='relative hover:bg-white/10 h-7 w-7 p-0'
        data-testid='notification-bell'
      >
        <Bell className='w-4 h-4 text-white' />
        {unreadCount > 0 && (
          <Badge 
            className='absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center p-0 bg-red-600 text-white text-[10px]'
            data-testid='notification-count'
          >
            {unreadCount}
          </Badge>
        )}
      </Button>

      {showPanel && (
        <Card className='absolute right-0 top-10 w-80 max-h-[500px] shadow-lg border-slate-200 z-50'>
          <div className='p-4 border-b border-slate-200 bg-primary/5'>
            <h3 className='text-lg font-semibold text-slate-900'>Notificaciones</h3>
            <p className='text-xs text-slate-600'>{unreadCount} sin leer</p>
          </div>

          <ScrollArea className='h-[500px]'>
            {alertas.length === 0 ? (
              <div className='p-8 text-center'>
                <Bell className='w-12 h-12 text-slate-300 mx-auto mb-3' />
                <p className='text-sm text-slate-500'>No hay notificaciones</p>
              </div>
            ) : (
              <div className='p-2'>
                {alertas.map((alerta) => (
                  <div
                    key={alerta.id}
                    onClick={() => handleAlertaClick(alerta)}
                    className={`p-4 mb-2 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                      alerta.leida ? 'bg-white border-slate-100 opacity-70' : getAlertColor(alerta.tipo)
                    }`}
                    data-testid={`alerta-${alerta.id}`}
                  >
                    <div className='flex items-start gap-3'>
                      <span className='text-2xl'>{getAlertIcon(alerta.tipo)}</span>
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center justify-between mb-1'>
                          <span className='text-xs font-medium text-slate-600'>{alerta.tipo}</span>
                          {!alerta.leida && (
                            <div className='w-2 h-2 bg-primary rounded-full' />
                          )}
                        </div>
                        <p className='text-sm text-slate-900 leading-snug mb-2'>
                          {alerta.mensaje}
                        </p>
                        <p className='text-xs text-slate-500'>
                          {new Date(alerta.created_at).toLocaleString('es-CL', {
                            dateStyle: 'short',
                            timeStyle: 'short'
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </Card>
      )}
    </div>
  );
};

export default NotificationBell;
